// packages/console/src/components/panels/LiveEncoderPanel.tsx
//
// Transcode-ladder table for the LIVE console, ported from the "Transcode
// ladder" block of docs/design/ui_kits/aether-live/Transcoder.jsx. Each rung is
// a REAL LiveRung off the SSE stream: per-rung enable toggle, name, codec,
// profile, target bitrate, measured bitrate with a Meter, GPU share, and encode
// state. Audio is a fixed final row.
//
// Selecting a rung drives the Rendition params panel on the right. The enable
// toggle is a control (onToggle) the parent applies; measured/GPU/state are
// read-only from the stream. Honest absence (R10): an off/idle rung shows an
// em-dash for measured and GPU, and a stopped session leaves the whole table at
// its idle state rather than fabricating encode figures.

import { useState } from "react";
import { Panel, Dot, Meter, TChip } from "../Parts";
import { Icon } from "../Icons";
import type { LiveRung } from "../../api/live";
import { mintPlaybackUrl } from "../../api/liveControl";
import { whepUrlFromPlayback } from "../../api/whep";
import { servicePaths } from "../../api/config";
import { firstActiveRungName, PLAY_PARAM, WHEP_PARAM, TITLE_PARAM } from "../../lib/livePlayback";
import { EM_DASH, fmtMbps, fmtPct, mbps } from "../../lib/format";

const COLS = "38px 96px 78px 88px 92px 1fr 74px 70px 132px";

function rungStateLabel(r: LiveRung, onAir: boolean): { tone: "ok" | "idle"; label: string } {
  if (!r.on) return { tone: "idle", label: "Off" };
  // An enabled rung is encoding whenever the session is on air (the authoritative
  // signal); r.state from telemetry is a secondary trigger. This keeps the rung
  // status honest instead of reading "Idle" while frames are clearly flowing.
  if (onAir || r.state === "encoding") return { tone: "ok", label: "Encoding" };
  return { tone: "idle", label: "Idle" };
}

// PlaybackActions are the Play + Copy buttons that sit on the first active rung.
// Both mint a fresh, self-authenticating playback URL for the live ABR MASTER
// (multivariant index.m3u8) and then either open it in a new player tab (Play,
// so the player does ABR across the whole ladder) or copy it to the clipboard
// (Copy, for pasting into VLC). The mint call's pending state disables both
// buttons; a failure surfaces unobtrusively as a red hint in place of the label.
function PlaybackActions({ streamId, rungName }: { streamId: string; rungName: string }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(false);

  // mint asks the control plane for a fresh URL; both actions share it so a click
  // always plays/copies a live, unexpired link (never a stale cached one).
  const mint = async (): Promise<string | null> => {
    setErr(false);
    setBusy(true);
    try {
      const { url } = await mintPlaybackUrl(streamId);
      // The mint returns a path relative to the live service (/v1/live-streams/...).
      // Prefix the live proxy base (/api/live) and make it absolute so the player
      // tab AND an external player (VLC) resolve it: neither has the console's
      // relative-path context, and the token rides the path so no header is needed.
      return `${window.location.origin}${servicePaths.live}${url}`;
    } catch {
      setErr(true);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const play = async () => {
    const url = await mint();
    if (!url) return;
    // Open the standalone player tab at this origin. The master URL is self-
    // authenticating, so the new tab (which holds no in-memory session token)
    // plays it directly and hls.js does ABR across the ladder.
    const q = `?${PLAY_PARAM}=${encodeURIComponent(url)}&${TITLE_PARAM}=${encodeURIComponent(rungName)}`;
    window.open(`${window.location.origin}/${q}`, "_blank", "noopener,noreferrer");
  };

  // webrtc opens the standalone WebRTC (WHEP) player tab: it mints the same
  // playback token, derives the WHEP URL from it, and hands it to the AV1 sub-second
  // player. AV1-over-WebRTC needs Chrome/Edge; the player tab falls back with a hint.
  const webrtc = async () => {
    setErr(false);
    setBusy(true);
    try {
      const { url } = await mintPlaybackUrl(streamId);
      const whep = whepUrlFromPlayback(streamId, url);
      if (!whep) {
        setErr(true);
        return;
      }
      const q = `?${WHEP_PARAM}=${encodeURIComponent(whep)}&${TITLE_PARAM}=${encodeURIComponent(rungName)}`;
      window.open(`${window.location.origin}/${q}`, "_blank", "noopener,noreferrer");
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    const url = await mint();
    if (!url) return;
    // Reuse the LiveIngestPanel clipboard idiom: navigator.clipboard with a brief
    // "Copied" confirmation and a silent, honest fallback when unavailable (an
    // embedded webview), never faking success.
    void navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {});
  };

  const btn: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    minHeight: 26,
    padding: "0 8px",
    borderRadius: "var(--r-sm)",
    border: "1px solid var(--line-strong)",
    background: "transparent",
    color: "var(--fg2)",
    font: "600 9px var(--font-sans)",
    letterSpacing: ".08em",
    textTransform: "uppercase",
    cursor: busy ? "default" : "pointer",
    opacity: busy ? 0.6 : 1,
    whiteSpace: "nowrap",
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ display: "flex", alignItems: "center", gap: 5 }}
      title="Play or copy the live ABR playback URL"
    >
      <button className="ae-b" onClick={play} disabled={busy} aria-label={`${rungName} play`} style={btn}>
        <Icon name="play" size={11} fill="currentColor" />
        {err ? <span style={{ color: "var(--err)" }}>Retry</span> : "Play"}
      </button>
      <button
        className="ae-b"
        onClick={webrtc}
        disabled={busy}
        aria-label={`${rungName} webrtc`}
        title="Open the sub-second WebRTC (AV1) player (Chrome/Edge)"
        style={{ ...btn, color: "var(--blue-400)" }}
      >
        ⚡ WebRTC
      </button>
      <button
        className="ae-b"
        onClick={copy}
        disabled={busy}
        aria-label={`${rungName} copy playback url`}
        style={{ ...btn, color: copied ? "var(--blue-400)" : "var(--fg2)" }}
      >
        {copied && <Icon name="check" size={11} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export function LiveEncoderPanel({
  rungs,
  sel,
  onSelect,
  onToggle,
  onAir,
  streamId,
  audioDisabled,
}: {
  rungs: LiveRung[];
  sel: string | null;
  onSelect: (rungName: string) => void;
  onToggle: (rungName: string, on: boolean) => void;
  onAir: boolean;
  // The provisioned stream's id. Play/Copy on the first active rung mint a
  // playback URL against it; absent (no stream yet), the actions are not shown.
  streamId?: string | null;
  // audioDisabled reflects the active preset: when the source has no audio the
  // preset disables the AAC lane, and the Audio row reads "Off" (no wasted 128k).
  audioDisabled?: boolean;
}) {
  // The first enabled rung anchors the Play/Copy actions (they target the ABR
  // master, so which rung is irrelevant to playback; this is just where they sit).
  const anchorRung = firstActiveRungName(rungs);
  return (
    <Panel
      title="Transcode ladder"
      bodyStyle={{ padding: 0, flex: 1, overflowY: "auto" }}
      style={{ flex: 1, minHeight: 0 }}
      right={
        <div style={{ display: "flex", gap: 6 }}>
          <TChip active>ABR ladder</TChip>
          <TChip>Passthrough</TChip>
        </div>
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: COLS,
          alignItems: "center",
          padding: "0 13px",
          height: 32,
          borderBottom: "1px solid var(--line)",
          font: "500 9px var(--font-sans)",
          letterSpacing: ".13em",
          textTransform: "uppercase",
          color: "var(--fg4)",
          position: "sticky",
          top: 0,
          background: "var(--bg-panel)",
          zIndex: 2,
        }}
      >
        <span>On</span>
        <span>Rendition</span>
        <span>Codec</span>
        <span>Profile</span>
        <span>Target</span>
        <span>Measured</span>
        <span>GPU</span>
        <span>State</span>
        <span>Playback</span>
      </div>
      {rungs.length === 0 ? (
        <div style={{ padding: "16px 13px", font: "var(--t-micro)", color: "var(--fg4)" }}>
          {onAir ? "No ladder reported" : "Ladder idle"}
        </div>
      ) : (
        rungs.map((r) => {
          const on = r.on;
          const measured = mbps(r.measuredBitsPerSec);
          const target = mbps(r.targetBitsPerSec);
          const pct = on && measured !== null && target && target > 0 ? (measured / target) * 100 : 0;
          const st = rungStateLabel(r, onAir);
          return (
            <div
              key={r.rungName}
              onClick={() => onSelect(r.rungName)}
              style={{
                display: "grid",
                gridTemplateColumns: COLS,
                alignItems: "center",
                padding: "0 13px",
                height: 44,
                cursor: "pointer",
                borderBottom: "1px solid var(--line)",
                background: sel === r.rungName ? "var(--blue-tint)" : "transparent",
                boxShadow: sel === r.rungName ? "inset 2px 0 0 var(--blue-500)" : "none",
                opacity: on ? 1 : 0.45,
              }}
            >
              <button
                className="ae-b"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(r.rungName, !on);
                }}
                aria-pressed={on}
                aria-label={`${r.rungName} enable`}
                style={{
                  width: 26,
                  height: 15,
                  borderRadius: 999,
                  cursor: "pointer",
                  position: "relative",
                  padding: 0,
                  background: on ? "var(--blue-500)" : "var(--bg-hover)",
                  border: "none",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 2,
                    left: on ? 13 : 2,
                    width: 11,
                    height: 11,
                    borderRadius: "50%",
                    background: "#fff",
                    transition: "left var(--dur-fast) var(--ease-std)",
                  }}
                />
              </button>
              <span style={{ font: "500 13px var(--font-mono)", color: "#fff" }}>{r.rungName}</span>
              <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)" }}>{r.codec || EM_DASH}</span>
              <span style={{ font: "var(--t-body-sm)", color: "var(--fg3)" }}>{r.profile || EM_DASH}</span>
              <span
                style={{
                  font: "400 12px var(--font-mono)",
                  color: "var(--fg2)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {target !== null ? `${fmtMbps(r.targetBitsPerSec, 1)} Mb/s` : EM_DASH}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 10, paddingRight: 16 }}>
                <span
                  style={{
                    font: "400 12px var(--font-mono)",
                    color: on && measured !== null ? "#fff" : "var(--fg4)",
                    width: 46,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {on && measured !== null ? fmtMbps(r.measuredBitsPerSec) : EM_DASH}
                </span>
                <div style={{ flex: 1 }}>
                  <Meter pct={pct} color={on ? "var(--blue-500)" : "var(--idle)"} />
                </div>
              </div>
              <span
                style={{
                  font: "400 12px var(--font-mono)",
                  color: "var(--fg2)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {on && r.gpuSharePct !== null ? fmtPct(r.gpuSharePct) : EM_DASH}
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  font: "var(--t-micro)",
                  color: "var(--fg2)",
                }}
              >
                <Dot tone={st.tone} />
                {st.label}
              </span>
              {/* Play/Copy live only on the FIRST ACTIVE rung, and only once a
                  stream is provisioned. They target the ABR master, not this rung. */}
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                {streamId && anchorRung === r.rungName && (
                  <PlaybackActions streamId={streamId} rungName={r.rungName} />
                )}
              </div>
            </div>
          );
        })
      )}
      {/* Audio: a fixed final row. It uses the SAME column grid as the rungs so its
          state sits under the State column, aligned with the video rungs. When the
          preset disables audio (the source has none) it reads "Off" with em-dashes
          for codec/bitrate, so no phantom 128k is implied. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: COLS,
          alignItems: "center",
          padding: "0 13px",
          height: 44,
          borderBottom: "1px solid var(--line)",
          opacity: audioDisabled ? 0.5 : 1,
        }}
      >
        <Icon name="audio-lines" size={14} color="var(--viz-3)" />
        <span style={{ font: "500 13px var(--font-mono)", color: "#fff" }}>Audio</span>
        <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)" }}>{audioDisabled ? EM_DASH : "AAC-LC"}</span>
        <span />
        <span style={{ font: "400 12px var(--font-mono)", color: "var(--fg3)" }}>
          {audioDisabled ? EM_DASH : "128 kb/s"}
        </span>
        <span />
        <span />
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            font: "var(--t-micro)",
            color: "var(--fg2)",
          }}
        >
          <Dot tone={audioDisabled ? "idle" : onAir ? "ok" : "idle"} />
          {audioDisabled ? "Off" : onAir ? "Encoding" : "Idle"}
        </span>
        <span />
      </div>
    </Panel>
  );
}
